import { Button } from "./ui/button";
import {
  PhoneCall,
  Headphones,
  MessageSquare,
  CalendarCheck,
  Workflow,
  Repeat2,
  Filter,
  Briefcase,
  Settings2,
  ArrowRight,
} from "lucide-react";

const services = [
  { Icon: PhoneCall, title: "AI Voice Agents", desc: "24/7 inbound & outbound calls that book, qualify, and follow up." },
  { Icon: Headphones, title: "AI Receptionists", desc: "Never miss a lead. Greet, route, and capture every caller automatically." },
  { Icon: MessageSquare, title: "AI Customer Service", desc: "Resolve common questions across SMS, web chat, and email instantly." },
  { Icon: CalendarCheck, title: "AI Appointment Booking", desc: "Two-way calendar sync, reminders, and confirmations — hands-free." },
  { Icon: Workflow, title: "CRM Workflow Automation", desc: "Pipelines, tags, and stage transitions trigger themselves." },
  { Icon: Repeat2, title: "AI Follow-Up Systems", desc: "Multi-touch nurture sequences that recover dead leads on autopilot." },
  { Icon: Filter, title: "Lead Qualification AI", desc: "Score, route, and assign every lead to the right rep in real time." },
  { Icon: Briefcase, title: "AI Sales Assistants", desc: "Live call coaching, quote generation, and proposal automation." },
  { Icon: Settings2, title: "Operational Automation", desc: "Custom AI workflows wired into your tools, your data, your business." },
];

const AIEmployeesSection = () => {
  return (
    <section className="py-20">
      <div className="text-center mb-14 max-w-3xl mx-auto px-4">
        <p className="text-xs md:text-sm font-bold tracking-[0.25em] text-primary mb-3">
          AI EMPLOYEES &amp; INTELLIGENT AUTOMATION
        </p>
        <h2 className="font-orbitron font-bold text-3xl md:text-5xl mb-4 text-foreground leading-tight">
          Your Business Deserves AI Employees{" "}
          <span className="text-primary">That Work 24/7</span>
        </h2>
        <p className="text-base md:text-lg text-muted-foreground">
          A full AI workforce — answering, booking, qualifying, and following up — wired into the systems you already use.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
        {services.map(({ Icon, title, desc }) => (
          <div
            key={title}
            className="group relative p-6 rounded-2xl border border-[hsl(var(--line))] glass-effect hover:border-primary/50 hover:-translate-y-1 transition-all duration-500"
            style={{ boxShadow: "0 6px 24px rgba(0,0,0,0.35)" }}
          >
            <div className="flex items-start gap-4">
              <div className="shrink-0 inline-flex items-center justify-center w-11 h-11 rounded-xl bg-primary/10 border border-primary/30 text-primary group-hover:bg-primary/20 group-hover:scale-110 transition-all duration-300">
                <Icon className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <h3 className="font-orbitron font-bold text-base md:text-lg text-foreground mb-1.5 leading-tight">
                  {title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
              </div>
            </div>

            {/* Hover glow */}
            <div
              className="pointer-events-none absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
              style={{
                background:
                  "radial-gradient(circle at 80% 0%, hsl(var(--secondary) / 0.12), transparent 60%)",
              }}
            />
          </div>
        ))}
      </div>

      <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-3">
        <Button
          asChild
          size="lg"
          className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold px-7"
        >
          <a
            href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2"
          >
            Book a Free AI Demo
            <ArrowRight className="w-4 h-4" />
          </a>
        </Button>
        <Button
          asChild
          size="lg"
          variant="outline"
          className="border-2 border-secondary/40 text-secondary hover:bg-secondary/10 hover:border-secondary font-bold px-7"
        >
          <a href="/ai-automation">Explore AI Services</a>
        </Button>
      </div>
    </section>
  );
};

export default AIEmployeesSection;
